import type { DragAnimation } from '@atlassian/jira-business-collaboration';
import { getDraggableIssueNode, getDraggableColumnNode } from '../get-draggable-node';
import { isElementInViewport, isColumnInViewport, isPrevElementInViewport } from '../viewport';

const COLUMN_MARGIN = 6;
const COLUMN_HEADER_SIZE = 60;

// returns coordinates of start and end position of the animated card
// eslint-disable-next-line complexity
export const getAnimatedCardPosition = ({
	animation,
	scrollElement,
	collapsedColumns,
}: {
	animation?: DragAnimation | null;
	scrollElement?: HTMLElement | null;
	collapsedColumns?: string[];
}) => {
	if (animation == null || !scrollElement) {
		return null;
	}

	const { sourceNodeId, destinationNodeId, destinationRelativeNodeId, destinationColumnId } =
		animation;

	const sourceElement = sourceNodeId != null ? getDraggableIssueNode(Number(sourceNodeId)) : null;
	const destinationElement =
		destinationNodeId != null ? getDraggableIssueNode(destinationNodeId) : null;
	const destinationPrevElement =
		destinationRelativeNodeId != null ? getDraggableIssueNode(destinationRelativeNodeId) : null;
	const destinationColumnElement =
		destinationColumnId != null ? getDraggableColumnNode(destinationColumnId) : null;

	if (
		!sourceElement ||
		(!destinationElement && !destinationPrevElement && !destinationColumnElement)
	) {
		return null;
	}
	const sourceElementRect = sourceElement.getBoundingClientRect();
	const scrollElementRect = scrollElement.getBoundingClientRect();

	if (!isElementInViewport(sourceElementRect, scrollElementRect)) {
		return null;
	}
	const start = { x: sourceElementRect.left, y: sourceElementRect.top };
	let end = null;

	if (destinationElement) {
		const destinationElementRect = destinationElement.getBoundingClientRect();
		if (!isElementInViewport(destinationElementRect, scrollElementRect)) {
			return null;
		}

		end = { x: destinationElementRect.left, y: destinationElementRect.top };
	} else if (destinationPrevElement) {
		const destinationPrevElementRect = destinationPrevElement.getBoundingClientRect();
		if (
			!isPrevElementInViewport(destinationPrevElementRect, sourceElementRect, scrollElementRect)
		) {
			return null;
		}

		end = {
			x: destinationPrevElementRect.left,
			y: destinationPrevElementRect.top + destinationPrevElementRect.height,
		};
	} else if (destinationColumnElement) {
		const destinationColumnElementRect = destinationColumnElement.getBoundingClientRect();
		if (!isColumnInViewport(destinationColumnElementRect, scrollElementRect)) {
			return null;
		}

		if (collapsedColumns && collapsedColumns.includes(String(destinationColumnId))) {
			end = {
				x:
					destinationColumnElementRect.left -
					COLUMN_HEADER_SIZE -
					destinationColumnElementRect.width,
				y: destinationColumnElementRect.top,
			};
		} else {
			end = {
				x: destinationColumnElementRect.left + COLUMN_MARGIN,
				y: destinationColumnElementRect.top + COLUMN_HEADER_SIZE,
			};
		}
	}

	if (!end) {
		return null;
	}

	return { start, end };
};
